import { useContext } from 'react'

import {
  Box, Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCampaignContact from '@hooks/useCampaignContact'
import useContact, { CreateOrEditContactCustomPayload, useContactForm } from '@hooks/useContact'

import countryCallingCodes from '@functions/countryCallingCodes'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { ContactModel } from '@models/types'

import ContactAddress from './components/ContactAddress'
import Options from './components/Options'
import User from './components/User'

const MODAL_KEY = 'CreateOrEditContactModal'

type ModalDataPayload = CreateOrEditContactCustomPayload & {
  contact: Partial<ContactModel>,
}

type ModalCallbacks = {
  closeModal: () => void,
  createContact: (payload: HandleCallbackActionParams) => void,
  createContactUser: (payload: HandleCallbackActionParams) => void,
  shareMobileLink: (payload: HandleCallbackActionParams) => void,
  updateContact: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditContactModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const {
    closeModal, createContact, createContactUser, shareMobileLink, updateContact,
  } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload: ModalDataPayload = digObject(modalData, MODAL_KEY, {})
  const initContact = digObject(modalPayload, 'contact', {})
  const campaign = digObject(modalPayload, 'campaign', {})
  const contextCallbacks = digObject(modalPayload, 'callbacks', {})

  // This modal is usually used to deal with campaign contacts.
  // We can use it to create a campaign contact if the createWithContact function is in
  // modal data, or we can use it to remove a contact from a campaign if the deleteCampaignContact
  // function is in modal data.
  const { createFn, deleteFn } = contextCallbacks

  const newRecord = !initContact.id

  const contactPayload = useContact(initContact)

  const {
    callbacks: {
      createContact: createContactFn,
      deleteContact,
      updateContact: updateFn,
    },
    creating,
    updating,
    user,
  } = contactPayload

  const { creating: creatingCampaignContact } = useCampaignContact()

  const contactFormPayload = useContactForm(initContact, { validateOn: 'change' })

  const {
    entityState,
    entityState: {
      company_name,
      contact_type,
      country_calling_code,
      email,
      first_name,
      last_name,
      legal_name,
      mobile,
    },
    errors,
    handlers,
    resetDefaultState,
    saveEnabled,
  } = contactFormPayload

  const createOrUpdateContactPayload = {
    callbacks: {
      action: newRecord ? createFn || createContactFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Contact ${newRecord ? 'Created' : 'Updated'}`,
    requestOptions: {
      include_campaign_contact_contact: true,
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Contact" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* First Name" errorMessage={errors.first_name}>
            <input name="first_name" type="text" value={first_name} {...handlers} />
          </Form.Field>

          <Form.Field label="* Last Name" errorMessage={errors.last_name} marginTop="large">
            <input name="last_name" type="text" value={last_name} {...handlers} />
          </Form.Field>

          <Form.Field label="Legal Name" marginTop="large">
            <input name="legal_name" type="text" value={legal_name} {...handlers} />
          </Form.Field>

          <Form.Field label="Company Name" marginTop="large">
            <input name="company_name" type="text" value={company_name} {...handlers} />
          </Form.Field>

          <Form.Field label="* Email" errorMessage={errors.email} marginTop="large">
            <input name="email" type="text" value={email} {...handlers} />
          </Form.Field>

          <Form.Field boxProps={{ flex: 1 }} label="Mobile" marginTop="large">
            <Box style={{ flexDirection: 'row', marginRight: 'small', width: '100%' }}>
              {/*
               // @ts-expect-error TS2322 */}
              <select
                name="country_calling_code"
                value={country_calling_code}
                {...handlers}
              >
                <option value="">Select...</option>

                {countryCallingCodes.map(({ key, value, title }) => (
                  <option key={key} value={value}>
                    {title}
                  </option>
                ))}
              </select>

              <Form.Field boxProps={{ marginLeft: 'small', width: '70%' }}>
                <input type="text" value={mobile} name="mobile" {...handlers} />
              </Form.Field>
            </Box>
          </Form.Field>

          <Form.Field label="Contact Type" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              name="contact_type"
              value={contact_type}
              {...handlers}
            >
              <option value="">Select Contact Type</option>
              <option value="vendor">Vendor</option>
            </select>
          </Form.Field>

          <ContactAddress contactFormPayload={contactFormPayload} />

          {!newRecord && (
            <User callbacks={{ createContactUser }} contactPayload={contactPayload} />
          )}

          <Options
            callbacks={{
              closeModal,
              deleteFn: deleteFn || deleteContact,
              resetDefaultState,
              shareMobileLink,
            }}
            campaign={campaign}
            // @ts-expect-error TS2740
            user={user}
            isNewRecord={newRecord}
          />
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || creatingCampaignContact || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createContact(createOrUpdateContactPayload)
              : () => updateContact(createOrUpdateContactPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Contact` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditContactModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
